import { useCallback } from 'react';
import { saveOnboarding } from '@src/services';
import {
  OnboardingReq,
  PaymentFormValues,
  TOnboardingPayment,
  TradingPlatForms,
} from '@src/types';
import { toast } from 'react-toastify';
import { Navigate, useNavigate } from 'react-router-dom';
import { useCountryOptions } from '@hooks/useCountryOptions.ts';
import { useStateOptions } from '@hooks/useStateOptions.ts';
import { ReviewCheckoutForm } from '@pages/user/checkout/ReviewCheckoutForm.tsx';
import { useRenewStore } from '@stores/renewStore.ts';

export const RenewReviewCheckoutPage = () => {
  const { platform, account, payment, setPayment, reset } = useRenewStore();
  const { countryOptions } = useCountryOptions();
  const { stateOptions } = useStateOptions();
  const navigate = useNavigate();

  const handleConfirm = useCallback(
    async (data: PaymentFormValues) => {
      try {
        const payload: TOnboardingPayment = {
          terms_accepted: payment?.terms_accepted || [],
          referral_source: data.referral_source,
          payment_type: data.payment_type,
          coupon_code: data.coupon_code,
          billing_info: {
            street: data.street,
            city: data.city,
            country: data.country,
            state: data.state,
            zip_code: data.zip_code,
            phone_number: data.phone_number,
            first_name: data.first_name,
            last_name: data.last_name,
          },
        };

        setPayment(payload);

        const onboardingRequest: OnboardingReq = {
          trading_platform: platform.replace(/-\d+$/, '') as TradingPlatForms,
          terms_accepted: payload.terms_accepted,
          referral_source: data.referral_source,
          checkout_info: {
            package_id: account?.id,
            addons: account?.selectedAddonIds ?? [],
            payment_type: data?.payment_type,
            coupon_code: data.coupon_code,
            billing_info: payload.billing_info,
          },
        };

        if (!platform || !account) {
          toast.error("You're almost there! Finish all 3 steps to proceed.");

          return;
        }
        const res = await saveOnboarding(onboardingRequest);

        reset();
        if (!res.data?.url) {
          navigate('/', { replace: true });

          return;
        }
        window.location.href = res.data.url;
      } catch {
        toast.error('Something went wrong. Please try again later.');
      }
    },
    [account, navigate, payment?.terms_accepted, platform, reset, setPayment]
  );

  if (!account) {
    return <Navigate to={'/renew/select-account'} />;
  }

  return (
    <ReviewCheckoutForm
      isRenew
      account={account}
      countries={countryOptions}
      payment={payment}
      platform={platform}
      states={stateOptions}
      onBack={() => navigate('/renew/select-account')}
      onSubmit={handleConfirm}
    />
  );
};
